import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  Activity,
  Bell,
  Briefcase,
  Calculator,
  ChevronsLeft,
  ClipboardCheck,
  ClipboardList,
  Coins,
  DollarSign,
  FileSpreadsheet,
  FileText,
  FolderArchive,
  Gavel,
  Handshake,
  House,
  Inbox,
  Landmark,
  LayoutDashboard,
  Menu,
  MessageSquare,
  Receipt,
  Scale,
  ScrollText,
  Settings,
  Users,
  Wallet,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/design-system/lib/utils";
import { tabsForUser, type PortalTabId, type PortalTabSpec } from "./PortalTabMatrix";
import { usePortalAuth } from "../PortalAuthContext";
import { usePortalNotifications } from "./PortalNotificationsContext";
import { useNewConnectionBadge } from "./NewConnectionBadgeContext";
import { requestsBadgeFigureFor, useRequestsBadge } from "./PortalRequestsBadgeContext";
import { leadGenFeedbackApi } from "../api/leadGenFeedback";

interface PortalSidebarProps {
  activeTab: PortalTabId;
  onSelect: (next: PortalTabId) => void;
}

// Post-Rollout PR 2 (PROB-13) — portal-side sidebar that visually
// matches the internal app's chrome (gold-on-onyx, group header + items,
// gold active-state border-l accent) without depending on the internal
// auth/permissions context. Built as a sibling rather than a refactor
// of components/layout/Sidebar.tsx because that primitive is tightly
// coupled to useAuth + useReunderwritingV1 + useSidebarGroups + the
// backend-driven SidebarEntryDto pipeline, none of which the portal
// shell has access to.
//
// Entries are sourced from PortalTabMatrix.tabsForUser so the lineup
// stays role-correct (Broker vs Buyer vs Seller vs Vendor).

const TAB_ICONS: Partial<Record<PortalTabId, LucideIcon>> = {
  home: House,
  requests: Inbox,
  documents: FileText,
  files: FolderArchive,
  loans: Landmark,
  "debt-collector-language": ScrollText,
  bills: Receipt,
  distributions: Coins,
  commissions: Wallet,
  collections: Gavel,
  "tax-forms": FileSpreadsheet,
  payments: DollarSign,
  // Broker Commission Modeler — the broker's private what-if scratchpad.
  "commission-modeler": Calculator,
  "payment-setup": Landmark,
  notifications: Bell,
  contributions: Coins,
  invoices: Receipt,
  deals: Briefcase,
  // Partner Brand Ownership — the caller's own prequal-site completions.
  prequalification: ClipboardCheck,
  // Annual Vendor Audit — scales, for the market comparison. ⚠️ TAB_ICONS is
  // `Partial`, so a missing entry renders a generic LayoutDashboard with no
  // error anywhere; this map is the only thing that catches it.
  "annual-audit": Scale,
  vendors: Handshake,
  customers: Users,
  // Brokerage / Sub-Broker portal — the affiliated-sub-broker roster + firm feed.
  roster: Users,
  activity: Activity,
  // Brokerage / Sub-Broker PR-7 — the broker-side affiliation consent surface.
  affiliations: Handshake,
  // Lead Generation console sections (shared black rail).
  "lg-preleads": ClipboardList,
  "lg-earnings": DollarSign,
  "lg-feedback": MessageSquare,
  settings: Settings,
};

// Lead Generation console — the unread-feedback badge for the black-rail
// "Feedback" item. The count is fetched ONCE by the provider (mounted only by
// LeadGeneratorDashboardPage, above PortalChrome) and cleared to 0 by the
// Feedback tab once its `mine()` read lands (which marks read server-side).
// The default value is a no-op 0, so consuming without a provider (every other
// portal) never shows a badge or fires a fetch.
interface LeadGenFeedbackBadge {
  count: number;
  markRead: () => void;
}

const LeadGenFeedbackBadgeContext = createContext<LeadGenFeedbackBadge>({
  count: 0,
  markRead: () => {},
});

export function useLeadGenFeedbackBadge(): LeadGenFeedbackBadge {
  return useContext(LeadGenFeedbackBadgeContext);
}

export function LeadGenFeedbackBadgeProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Best-effort: a failed count must never break the console shell.
    leadGenFeedbackApi.unreadCount().then(
      (n) => {
        if (!cancelled) setCount(n);
      },
      () => {},
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const markRead = useCallback(() => setCount(0), []);

  return (
    <LeadGenFeedbackBadgeContext.Provider value={{ count, markRead }}>
      {children}
    </LeadGenFeedbackBadgeContext.Provider>
  );
}

export function PortalSidebar({ activeTab, onSelect }: PortalSidebarProps) {
  const auth = usePortalAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Vendor Connection v1 — unseen-connection badge for the "My Vendors" /
  // "Customers" entries; the count lives in NewConnectionBadgeProvider so
  // the tabs' first-view stamp can refresh it. Hook order: must run before
  // the early return.
  const { count: connectionCount } = useNewConnectionBadge();

  // Outstanding-requests badge — shared across every portal type (every
  // recipient account has a Requests tab), resolved to ONE figure here so
  // every item renders the same number. 0 outside the provider. Hook order:
  // must run before the early return.
  const { counts: requestsCounts } = useRequestsBadge();

  // Lead Generation console — unread coaching-feedback count for the "Feedback"
  // rail item. 0 (no provider) for every other portal. Hook order: before the
  // early return.
  const { count: feedbackCount } = useLeadGenFeedbackBadge();

  if (!auth.me) return null;

  const tabs = tabsForUser(auth.me.accountType, auth.me.kind, auth.me.brokerType);
  const requestsCount = requestsBadgeFigureFor(auth.me.accountType, requestsCounts);

  return (
    <>
      {/* Mobile hamburger — lives in the sidebar root so we can toggle
          the same drawer state from one place. The top bar and the drawer
          header both reserve its 64px column below md (pl-16) so it never
          covers the Secure Portal brand or the drawer's own title. */}
      <button
        type="button"
        className="md:hidden fixed top-4 left-4 z-50 inline-flex items-center justify-center h-10 w-10 rounded-md bg-onyx text-white-pure"
        onClick={() => setMobileOpen((v) => !v)}
        aria-label="Toggle navigation"
        aria-expanded={mobileOpen}
      >
        {mobileOpen ? (
          <X size={18} strokeWidth={1.5} aria-hidden />
        ) : (
          <Menu size={18} strokeWidth={1.5} aria-hidden />
        )}
      </button>

      {/* Scrim behind the open mobile drawer — tap outside to close. */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-onyx/50"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          data-testid="portal-sidebar-scrim"
        />
      )}

      <aside
        className={cn(
          "fixed md:static inset-y-0 left-0 z-40 h-screen md:h-auto md:min-h-screen",
          "bg-onyx text-white-pure flex flex-col transition-all duration-normal ease-standard",
          collapsed ? "md:w-16" : "md:w-64",
          "w-72 md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
        data-testid="portal-sidebar"
      >
        <div
          className={cn(
            "flex items-center h-16 pl-16 pr-space-3 md:px-space-3 border-b border-border-dark justify-between",
            collapsed && "md:justify-center",
          )}
        >
          {collapsed ? (
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              className="flex items-center justify-center"
              aria-label="Expand sidebar"
            >
              <LayoutDashboard size={20} strokeWidth={1.5} className="text-gold" aria-hidden />
            </button>
          ) : (
            <>
              <div className="flex items-center gap-space-2">
                <LayoutDashboard size={20} strokeWidth={1.5} className="text-gold" aria-hidden />
                <span className="font-display font-bold text-h5 text-white-pure">
                  Phoenix Capital
                </span>
              </div>
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                className="hidden md:inline-flex p-space-1 text-gold-shadow hover:text-gold"
                aria-label="Collapse sidebar"
              >
                <ChevronsLeft size={18} strokeWidth={1.5} aria-hidden />
              </button>
            </>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-space-3">
          {!collapsed && (
            <div
              className={cn(
                "px-space-4 py-space-1 text-[11px] font-display font-medium uppercase text-gold",
                "tracking-[1.5px]",
              )}
            >
              {auth.me.accountType} Portal
            </div>
          )}
          <ul className="space-y-[2px] pt-space-1">
            {tabs.map((tab) => (
              <PortalSidebarItem
                key={tab.id}
                tab={tab}
                active={tab.id === activeTab}
                collapsed={collapsed}
                connectionCount={connectionCount}
                feedbackCount={feedbackCount}
                requestsCount={requestsCount}
                onSelect={(id) => {
                  onSelect(id);
                  setMobileOpen(false);
                }}
              />
            ))}
          </ul>
        </nav>
      </aside>
    </>
  );
}

interface PortalSidebarItemProps {
  tab: PortalTabSpec;
  active: boolean;
  collapsed: boolean;
  /** Unseen-connection count (fetched once by the sidebar, not per item). */
  connectionCount: number;
  /** Unread lead-gen coaching-feedback count (0 outside the console). */
  feedbackCount: number;
  /** Outstanding-requests figure for the Requests entry, already resolved for
   *  this portal type by the sidebar (see requestsBadgeFigureFor) — buyer and
   *  seller show what needs THEM, everyone else the historical total. */
  requestsCount: number;
  onSelect: (id: PortalTabId) => void;
}

function PortalSidebarItem({ tab, active, collapsed, connectionCount, feedbackCount, requestsCount, onSelect }: PortalSidebarItemProps) {
  const Icon = TAB_ICONS[tab.id] ?? LayoutDashboard;
  const { unreadCount } = usePortalNotifications();
  const badgeCount =
    tab.id === "notifications"
      ? unreadCount
      : tab.id === "requests"
        ? requestsCount
        : tab.id === "vendors" || tab.id === "customers"
          ? connectionCount
          : tab.id === "lg-feedback"
            ? feedbackCount
            : 0;
  const showBadge = badgeCount > 0;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(tab.id)}
        aria-current={active ? "page" : undefined}
        // Keep the tab-* testid so the existing dashboard tests keep
        // working through the chrome change; portal-nav-* is the
        // forward-compatible name newer tests should prefer.
        data-testid={`tab-${tab.id}`}
        data-portal-nav={tab.id}
        className={cn(
          "w-full flex items-center gap-space-3 h-10 px-space-4 text-small",
          "font-display font-semibold tracking-wide text-left",
          "transition-colors duration-fast ease-standard",
          "text-white-pure/70 hover:text-white-pure hover:bg-white-pure/5",
          active &&
            "bg-gold/15 text-gold border-l-2 border-gold pl-[calc(theme(spacing.space-4)_-_2px)]",
        )}
      >
        <span className="relative shrink-0">
          <Icon size={18} strokeWidth={1.5} aria-hidden />
          {showBadge && collapsed && (
            <span
              className="absolute -top-[5px] -right-[5px] h-2 w-2 rounded-pill bg-gold"
              aria-hidden
            />
          )}
        </span>
        {!collapsed && (
          <span className="truncate flex-1 flex items-center">
            <span className="truncate">{tab.label}</span>
            {tab.comingSoon && (
              <span className="ml-2 text-[10px] uppercase tracking-label text-gold-shadow">
                Soon
              </span>
            )}
            {showBadge && (
              <span className="ml-auto min-w-[18px] h-[18px] px-[4px] rounded-pill bg-gold text-onyx text-[10px] font-bold flex items-center justify-center">
                {badgeCount > 99 ? "99+" : badgeCount}
              </span>
            )}
          </span>
        )}
      </button>
    </li>
  );
}
